<template>
    <div class="contact-view">
        <ContentLayout
            :image="heroImage"
            :height="400"
            :mobile-height="320"
            eyebrow="Contact"
            icon="mdi-message"
            title="Contactez-nous"
            subtitle="Besoin d'un véhicule, d'un lavage ou d'une réponse rapide? Appelez-nous ou écrivez-nous."
        >
            <template v-slot:hero-actions>
                <v-btn
                    x-large
                    color="secondary"
                    class="primary--text font-weight-bold mr-3 mb-3"
                    :href="'tel:' + $store.state.data.phone"
                >
                    <v-icon left>mdi-phone</v-icon>
                    {{ $store.state.data.phone }}
                </v-btn>
                <v-btn
                    x-large
                    outlined
                    color="white"
                    class="mb-3"
                    :href="'mailto:' + $store.state.data.email"
                >
                    <v-icon left>mdi-email</v-icon>
                    Courriel
                </v-btn>
            </template>

        <section class="contact-section">
            <v-container>
                <v-row align="start">
                    <v-col cols="12" lg="7">
                        <div class="overline secondary--text font-weight-bold mb-2">Nous joindre</div>
                        <h2 class="display-1 font-weight-bold mb-3">Choisissez la façon la plus simple de nous joindre.</h2>
                        <p class="body-1 grey--text text--darken-1 mb-6">
                            Notre équipe peut vous répondre par téléphone, courriel, Facebook ou Messenger.
                        </p>

                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-card
                                    class="contact-card pa-5"
                                    elevation="10"
                                    :href="'tel:' + $store.state.data.phone"
                                >
                                    <div class="d-flex align-center mb-4">
                                        <v-avatar color="primary" size="48" class="mr-4">
                                            <v-icon color="secondary">mdi-phone</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="title font-weight-bold">Téléphone</div>
                                            <div class="caption grey--text text--darken-1">Réponse rapide</div>
                                        </div>
                                    </div>
                                    <div class="contact-card-value primary--text font-weight-bold">
                                        {{ $store.state.data.phone }}
                                    </div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-card
                                    class="contact-card pa-5"
                                    elevation="10"
                                    :href="'mailto:' + $store.state.data.email"
                                >
                                    <div class="d-flex align-center mb-4">
                                        <v-avatar color="secondary" size="48" class="mr-4">
                                            <v-icon color="primary">mdi-email</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="title font-weight-bold">Courriel</div>
                                            <div class="caption grey--text text--darken-1">Questions et demandes</div>
                                        </div>
                                    </div>
                                    <div class="contact-card-value primary--text font-weight-bold">
                                        {{ $store.state.data.email }}
                                    </div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-card
                                    class="contact-card pa-5"
                                    elevation="10"
                                    :href="'https://facebook.com/' + $store.state.data.facebook"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <div class="d-flex align-center mb-4">
                                        <v-avatar color="primary" size="48" class="mr-4">
                                            <v-icon color="secondary">mdi-facebook</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="title font-weight-bold">Facebook</div>
                                            <div class="caption grey--text text--darken-1">Suivez nos nouveautés</div>
                                        </div>
                                    </div>
                                    <div class="contact-card-value primary--text font-weight-bold">
                                        Facebook
                                    </div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-card
                                    class="contact-card pa-5"
                                    elevation="10"
                                    :href="'https://m.me/' + $store.state.data.facebook"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <div class="d-flex align-center mb-4">
                                        <v-avatar color="secondary" size="48" class="mr-4">
                                            <v-icon color="primary">mdi-facebook-messenger</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="title font-weight-bold">Messenger</div>
                                            <div class="caption grey--text text--darken-1">Message direct</div>
                                        </div>
                                    </div>
                                    <div class="contact-card-value primary--text font-weight-bold">
                                        Messenger
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-col>

                    <v-col cols="12" lg="5">
                        <v-card dark color="primary" class="address-card pa-6 mb-4" elevation="10" :href="mapHref" target="_blank" rel="noopener">
                            <div class="d-flex align-center mb-4">
                                <v-avatar color="secondary" size="48" class="mr-4">
                                    <v-icon color="primary">mdi-map-marker</v-icon>
                                </v-avatar>
                                <div>
                                    <div class="title font-weight-bold">Notre adresse</div>
                                    <div class="caption grey--text text--lighten-1">Itinéraire Google Maps</div>
                                </div>
                            </div>
                            <div class="body-1 white--text font-weight-bold">
                                {{ $store.state.data.address }}
                            </div>
                        </v-card>

                        <v-card dark color="primary" class="hours-card" elevation="12">
                            <v-card-title class="secondary--text">
                                <v-icon left color="secondary">mdi-clock</v-icon>
                                Heures d'ouverture
                            </v-card-title>
                            <v-divider dark></v-divider>
                            <div class="py-4">
                                <template v-if="openingHours.length > 0">
                                    <v-row
                                        v-for="(item, i) in openingHours"
                                        :key="i"
                                        class="px-4 py-1"
                                        no-gutters
                                    >
                                        <v-col
                                            cols="6"
                                            class="font-weight-medium"
                                            :class="{ 'success--text': i == dow && !!item, 'error--text': i == dow && !item }"
                                        >
                                            <v-icon
                                                size="18"
                                                left
                                                :color="i == dow ? (item ? 'success' : 'error') : 'grey lighten-1'"
                                            >
                                                {{ i == dow ? "mdi-circle" : "mdi-circle-outline" }}
                                            </v-icon>
                                            {{ days[i] }}
                                        </v-col>
                                        <v-col
                                            cols="6"
                                            class="font-weight-medium text-right"
                                            :class="{ 'success--text': i == dow && !!item, 'error--text': i == dow && !item }"
                                        >
                                            {{ item ? item : "FERMÉ" }}
                                        </v-col>
                                    </v-row>
                                </template>
                                <div v-else class="px-6 py-4 grey--text text--lighten-1">
                                    Les heures d'ouverture seront affichées sous peu.
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </section>
        </ContentLayout>
    </div>
</template>

<script>
    import ContentLayout from "./../layouts/Content";

    export default {
        name: "contact-view",
        components: {
            ContentLayout
        },
        data: () => ({
            days: [
                "Dimanche",
                "Lundi",
                "Mardi",
                "Mercredi",
                "Jeudi",
                "Vendredi",
                "Samedi"
            ]
        }),
        computed: {
            heroImage() {
                return this.$store.state.data.page_header_img ? 
                    this.$store.state.data.page_header_img : require("./../../assets/img/hero.jpg");
            },
            dow() {
                return new Date().getDay();
            },
            mapHref() {
                return "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(this.$store.state.data.address);
            },
            openingHours() {
                return this.$store.state.data && this.$store.state.data.opening_hours ? 
                    this.$store.state.data.opening_hours.split(";") : [];
            }
        }
    };
</script>

<style scoped>
    .contact-view {
        background: #f5f6f8;
    }

    .contact-section {
        padding: 64px 0;
    }

    .contact-card,
    .address-card,
    .hours-card {
        border-radius: 8px;
    }

    .contact-card {
        height: 100%;
    }

    .contact-card,
    .address-card {
        border: 1px solid rgba(34, 34, 34, 0.08);
        transition: transform 180ms ease, box-shadow 180ms ease;
    }

    .contact-card:hover,
    .address-card:hover {
        transform: translateY(-4px);
    }

    .contact-card-value {
        overflow-wrap: anywhere;
    }

    .address-card,
    .hours-card {
        border: 1px solid rgba(235, 184, 43, 0.36);
    }

    @media (max-width: 599px) {
        .contact-section {
            padding: 44px 0;
        }
    }
</style>
